import { ContentMediaDownloadDto, ContentPostContentDownloadDto, MediaUploadDto } from 'collaboration-service';
import { Button, Flag, ISO_3166, ImgIcons, Label, LanguageSelect, Theme, getColor, getUriFromLinkByName, useBelowBreakpoint } from 'imaginarity-react-ui';
import * as _ from "lodash";
import * as React from 'react';
import { CellProps, Column, useBlockLayout, usePagination, useResizeColumns, useTable } from 'react-table';
import { languages } from 'services/Config';
import { useImgI18N } from 'services/ImgI18N';
import { useThemePart } from 'services/useAppTheme';
import { EPLContainer, EPLEditContainer, EPLLabelContainer } from '../EditPartLabelAndEditor';
import { BaseEditItem } from '../GeneralPostEdit';
import { useGPEState } from '../Helpers/UseGPEState';
import { MissingTexts, PostEditElementProps } from '../TypeImplementations/PostEdit';
import EditLanguageContent from './EditLanguageContent';
import { EditorsSC as T, columnActionWidth, columnFlagWidth } from './EditorsSC';
import { ThemeContext } from 'styled-components';
import LabelInfo from 'components/LabelInfo';

// const EditLanguageContents = <T extends BaseEditItem>(p: PostEditElementProps<T, ContentPostContentDownloadDto[]>) => {
interface EditLanguageContentsProps<T extends BaseEditItem> extends PostEditElementProps<T, ContentPostContentDownloadDto[]> {
    saveText?: string;
    saveIcon?: ImgIcons;
}
const EditLanguageContents = <T extends BaseEditItem>(p: EditLanguageContentsProps<T>) => {
    const { saveText, saveIcon } = p;
    const { value, setValue, /*resetValue,*/ originalValue, addFile } = useGPEState(p.getter);
    const [selectedRowId, setSelectedRowId] = React.useState<{ id: string, time: number } | undefined>();
    const [edit, setEdit] = React.useState<number>();
    const [editValue, setEditValue] = React.useState<ContentPostContentDownloadDto>();
    const { t, createT } = useImgI18N("admin");
    const t_for_g = React.useMemo(() => createT("general"), [createT]);
    const theme = React.useContext<Theme>(ThemeContext);
    const deleteEntry = React.useMemo(() => (idx: number) => {
        const v = _.clone(value);
        v.splice(idx, 1);
        setValue(v);
    }, [value, setValue]);

    const w = useThemePart(x => x.content.width) - columnActionWidth - columnFlagWidth;
    // const width = useThemePart((t: Theme) => t.content.width);
    // const isMobile = width < 450;
    const isMobile = useBelowBreakpoint("tablet");
    const columns = React.useMemo<Column<ContentPostContentDownloadDto>[]>(() => [
        {
            id: "Actions",
            padding: 0,
            width: columnActionWidth,
            align: "left",
            Header: () => t("actions"),
            Cell: (cell: CellProps<ContentPostContentDownloadDto>) => {
                const [delConfirmed, setDelConfirmed] = React.useState<boolean>(false);
                const edit = () => {
                    setEdit(cell.row.index);
                };
                const del = () => {
                    deleteEntry(cell.row.index);
                }
                return (
                    <>
                        <Button
                            floated="left"
                            icon={delConfirmed ? "times" : "delete"}
                            kind={delConfirmed ? "secondary" : "halfTransparentButton"}
                            iconColor={delConfirmed ? undefined : "@accentRed"}
                            onClick={() => setDelConfirmed(!delConfirmed)}
                        />
                        <Button
                            floated="left"
                            icon={delConfirmed ? "delete" : "edit"}
                            kind={delConfirmed ? "primary" : "halfTransparentButton"}
                            iconColor={delConfirmed ? undefined : "@accentBlue"}
                            onClick={delConfirmed ? del : edit}
                        />
                    </>
                )
            },
        },
        {
            id: "Flag",
            width: columnFlagWidth,
            align: "left",
            Header: () => t("language"),
            Cell: (p: CellProps<ContentPostContentDownloadDto>) => {
                const l = p.data[p.row.index].lng;
                const fs = l.split("-");
                const f = (fs.length > 1 ? fs[1] : fs[0].toUpperCase()) as ISO_3166;
                return <><span style={{ textAlign: "left", marginLeft: 5, width: 25 }}> <Flag name={f} size={16} /></span><span style={{ marginTop: 1, marginLeft: 10 }}>{t_for_g(fs[0])}</span></>
            },
        },
        {
            id: "Content",
            Header: () => t("content"),
            width: w,
            accessor: d => d,
            Cell: (p: CellProps<ContentPostContentDownloadDto>) => {
                const d = p.row.original;
                if (d.useContent) {
                    const l = getUriFromLinkByName(d.content);
                    return <>
                        <T.CellContainer>
                            {d.content.fileName}
                        </T.CellContainer>
                        {l &&
                            <a href={l} target="_blank" rel="noreferrer" style={{ float: "right", transform: "translateX(2px)" }}>
                                <Button kind="transparentButton" icon="link" tooltip={{ tooltipText: t("open podcast in new tab") }} />
                            </a>
                        }
                    </>
                }
                else
                    return (
                        <>
                            <T.CellContainer>
                                {d.link}
                            </T.CellContainer>
                            <a href={d.link} target="_blank" rel="noreferrer" style={{ float: "right", transform: "translateX(2px)" }}>
                                <Button kind="transparentButton" icon="link external" />
                            </a>
                        </>
                    );
            },
        }
    ], [deleteEntry, t, t_for_g, w]);

    const {
        getTableProps,
        getTableBodyProps,

        headerGroups,
        // rows,
        prepareRow,

        page, // Instead of using 'rows', we'll use page,
        // which has only the rows for the active page

        // The rest of these things are super handy, too ;)
        canPreviousPage,
        canNextPage,
        //        pageOptions,
        pageCount,
        gotoPage,
        nextPage,
        previousPage,
        // setPageSize,
        // state: { pageIndex, pageSize },

    } = useTable({ columns, data: value, initialState: { pageSize: 5 } },
        usePagination,
        useBlockLayout,
        useResizeColumns,
    );

    const lngs = React.useMemo(() => {
        return _.filter([...languages, "de-CH"], l => _.find(value, v => v.lng === l) === undefined);
    }, [value])

    React.useEffect(() => {
        if (edit !== undefined) {
            if (edit >= 0)
                setEditValue(value[edit]);
            else
                setEditValue({ lng: lngs[0], contentAttribute: "", useContent: true, thumbnails: [], content: {} as any as ContentMediaDownloadDto, link: "" });
        }
        else
            setEditValue(undefined);
    }, [edit, value, lngs])



    const addNew = React.useMemo(() => () => {
        setEdit(-1);
    }, []);

    const save = () => {
        if (editValue) {
            const v = _.clone(value);
            if (edit !== undefined && edit >= 0)
                v[edit] = editValue;
            else
                v.push(editValue)
            setValue(v);
        }
        setEdit(undefined);
    }
    const cancel = () => {
        setEdit(undefined);
    }
    const chgFile = (file: File | undefined, media: MediaUploadDto) => {
        (media as any).file = file;
        if (editValue) {
            setEditValue({ ...editValue, content: media as any as ContentMediaDownloadDto })
            addFile(editValue.lng, ["content"]);
        }
    }

    const chgMode = (useContent: boolean) => {
        if (editValue)
            setEditValue({ ...editValue, useContent: !useContent });
    }
    const reset = () => {
        //console.log('edit => ', edit);
        if (edit !== undefined && edit >= 0 && originalValue) {
            //console.log('originalValue => ', originalValue);
            setEditValue(originalValue[edit]);
        }
    }
    const chgLng = React.useMemo(() => (lng: string) => setEditValue(ev => ({ ...ev, lng } as any)), []);



    const saveDisabled = editValue?.content.fileName === undefined;

    return (
        <>
            <T.LanguageStringEditorSC.container hidden={false}>
                <T.LanguageStringEditorSC.table {...getTableProps() as any}>
                    <T.LanguageStringEditorSC.thead>
                        {// Loop over the header rows
                            headerGroups.map(headerGroup => (
                                // Apply the header row props
                                <T.LanguageStringEditorSC.tr {...headerGroup.getHeaderGroupProps() as any}>
                                    {// Loop over the headers in each row
                                        headerGroup.headers.map(column => {
                                            //console.log(column);
                                            return (
                                                // Apply the header cell props
                                                <T.LanguageStringEditorSC.th {...column.getHeaderProps() as any}>
                                                    {// Render the header
                                                        column.render('Header')}
                                                    {/*column.canResize && column.minWidth !== column.maxWidth &&
                                                    <LanguageStringEditorSC.resizer {...column.getResizerProps()} resizing={column.isResizing} />
                                                */}
                                                </T.LanguageStringEditorSC.th>
                                            )
                                        }
                                        )}

                                </T.LanguageStringEditorSC.tr>
                            ))}
                    </T.LanguageStringEditorSC.thead>
                    {/* Apply the table body props */}
                    <T.LanguageStringEditorSC.tbody {...getTableBodyProps() as any}>
                        {// Loop over the table rows
                            page.map(row => {
                                //console.log('row => ', row);
                                // Prepare the row for display
                                prepareRow(row)

                                const isSelected = row.id === selectedRowId?.id;
                                const onChg = (e: any) => {
                                    if (isSelected) {
                                        const deltaT = (new Date().getTime()) - (selectedRowId?.time ?? 0);
                                        if (deltaT <= 500) {
                                            //console.log("double click!")
                                            setEdit(row.index);
                                        }
                                        else
                                            setSelectedRowId({ id: row.id, time: new Date().getTime() });
                                    }
                                    else
                                        setSelectedRowId({ id: row.id, time: new Date().getTime() });
                                    //const { onChange } = row.getToggleRowSelectedProps();
                                    //if (onChange)
                                    //onChange(e);
                                }
                                return (
                                    // Apply the row props
                                    <T.LanguageStringEditorSC.tr {...row.getRowProps() as any} selected={row.isSelected}>
                                        {// Loop over the rows cells
                                            row.cells.map(cell => {
                                                return (
                                                    <T.LanguageStringEditorSC.td {...cell.getCellProps(T.LanguageStringEditorSC.cellProps) as any} onClick={onChg}>
                                                        {// Render the cell contents
                                                            cell.render('Cell')}
                                                    </T.LanguageStringEditorSC.td>
                                                )
                                            })}
                                    </T.LanguageStringEditorSC.tr>
                                )
                            })}
                    </T.LanguageStringEditorSC.tbody>
                </T.LanguageStringEditorSC.table>
                {editValue === undefined &&
                    <T.Grid
                        // shadowed 
                        bordered
                    >
                        <div>
                            {canPreviousPage &&
                                <Button floated='left' icon="chevron left double" onClick={() => gotoPage(0)} kind={!canPreviousPage ? "fullTransparentButton" : "cancelButton"} disabled={!canPreviousPage} iconColor={!canPreviousPage ? "@lightGrey" : "inherit"} tooltip={!canPreviousPage ? undefined : { tooltipText: "first page", position: "top" }} />
                            }
                            {canPreviousPage &&
                                <Button floated='left' icon="chevron left" onClick={() => previousPage()} kind={!canPreviousPage ? "fullTransparentButton" : "cancelButton"} disabled={!canPreviousPage} iconColor={!canPreviousPage ? "@lightGrey" : "inherit"} tooltip={!canPreviousPage ? undefined : { tooltipText: "previous page", position: "top" }} />
                            }
                            {canNextPage &&
                                <Button floated='left' icon="chevron right" onClick={() => nextPage()} kind={!canNextPage ? "fullTransparentButton" : "cancelButton"} disabled={!canNextPage} iconColor={!canNextPage ? "@lightGrey" : "inherit"} tooltip={!canNextPage ? undefined : { tooltipText: "next page", position: "top" }} />
                            }
                            {canNextPage &&
                                <Button floated='left' icon="chevron right double" onClick={() => gotoPage(pageCount - 1)} kind={!canNextPage ? "fullTransparentButton" : "cancelButton"} disabled={!canNextPage} iconColor={!canNextPage ? "@lightGrey" : "inherit"} tooltip={!canNextPage ? undefined : { tooltipText: "last page", position: "top" }} />
                            }

                            {/* <Button
                            floated='right'
                            icon="add"
                            onClick={() => setPageSize(pageSize + 1)}
                            kind={pageSize >= rows.length ? "fullTransparentButton" : "cancelButton"}
                            disabled={pageSize >= rows.length}
                            iconColor={pageSize >= rows.length ? "@lightGrey" : "inherit"}
                            tooltip={pageSize >= rows.length ? undefined : { tooltipText: "show more entries", position: "top" }}
                        />
                        <Button
                            floated='right'
                            icon="minus"
                            onClick={() => setPageSize(pageSize - 1)}
                            kind={pageSize < 2 ? "fullTransparentButton" : "cancelButton"}
                            disabled={pageSize < 2}
                            iconColor={pageSize < 2 ? "@lightGrey" : "inherit"}
                            tooltip={pageSize < 2 ? undefined : { tooltipText: "show less entries", position: "top" }}
                        /> */}
                            {page.length === 0 &&
                                <div style={{ paddingLeft: 10, fontSize: "0.9em" }}>{t(MissingTexts.podcastsMissing)}</div>
                                // <div style={{ paddingLeft: 10, color: getColor({ theme }, "@accentRed"), fontSize: "0.9em" }}>{t("Podcast is missing")}</div>
                            }
                        </div>
                        <div>
                            {/* {!isMobile &&
                            <T.TableInfo>
                                {t("page {{number}} of {{of}}", { number: pageIndex + 1, of: pageCount })}
                                <br />
                                {t("{{count}} of {{length}} entries are shown", { count: pageSize, length: rows.length })}
                            </T.TableInfo>
                        } */}
                            <Button
                                floated='right'
                                icon="add"
                                // kind={page.length === 0 ? "primary" : "halfTransparentButton"}
                                kind={value.length !== 0 ? "secondary" : "primary"}
                                tooltip={{ tooltipText: value.length > 0 ? t("add another podcast") : t("add new podcast"), position: "top" }}
                                onClick={addNew}
                                content={isMobile ? undefined : t("add")}
                            />
                            {/* <Button floated='right' icon="undo" disabled={lngs.length === 0} kind={lngs.length === 0 ? "fullTransparentButton" : "cancelButton"} iconColor={lngs.length === 0 ? "@lightGrey" : "inherit"} tooltip={lngs.length === 0 ? undefined : { tooltipText: t("undo changes"), position: "top" }} onClick={resetValue} content={isMobile ? undefined : t("reset")} /> */}
                        </div>
                    </T.Grid>
                }
            </T.LanguageStringEditorSC.container>
            <T.LanguageStringEditorSC.container hidden={editValue === undefined} style={{ marginTop: 0, overflowX: "auto", padding: 10, border: `1px solid ${getColor({ theme }, "@middleLightGrey")}`, borderTop: "none", background: getColor({ theme }, "@veryLightGrey") }}>
                <EPLContainer>
                    <EPLLabelContainer style={{ paddingRight: 16 }}>
                        <Label content={t("language")} icon="language" />
                        <LabelInfo infoText={t("edit language strings - language description")} />
                    </EPLLabelContainer>
                    <EPLEditContainer>
                        <LanguageSelect
                            languageList={editValue && _.find(lngs, l => l === editValue.lng) === undefined ? [...lngs, editValue.lng] : lngs}
                            translationFunction={t_for_g}
                            value={editValue?.lng ?? lngs[0]}
                            isDisabled={_.find(value, v => v.lng === editValue?.lng) !== undefined}
                            onChange={chgLng}
                        />
                    </EPLEditContainer>
                </EPLContainer>

                <EditLanguageContent
                    content={editValue}
                    useContentChanged={chgMode}
                    setContent={chgFile}
                    reset={reset}
                    selected={!saveDisabled}
                />

                <EPLContainer style={{ width: "calc(100% - 0px)" }}>
                    <EPLLabelContainer />
                    <EPLEditContainer>
                        <T.Grid shadowed bordered >
                            <div>
                                <Button floated='left' icon="times" kind={saveDisabled ? "secondary" : "halfTransparentButton"}
                                    // tooltip={{ tooltipText: "undo changes", position: "top" }}
                                    onClick={cancel} content="cancel" />
                            </div>
                            <div>
                                <Button
                                    floated='right'
                                    // icon="save"
                                    icon={saveIcon ?? "save"}
                                    onClick={save}
                                    // content="save"
                                    content={saveText ?? "save"}
                                    kind={saveDisabled ? "blank" : "primary"}
                                    disabled={saveDisabled}
                                    tooltip={saveDisabled ? { tooltipText: t("no file selected"), position: "top" } : undefined}
                                />
                            </div>
                        </T.Grid>
                    </EPLEditContainer>
                </EPLContainer>
            </T.LanguageStringEditorSC.container>

        </>
    )
}
export default EditLanguageContents;